import React, { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet";
import html2pdf from "html2pdf.js";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import Icon from "../../components/AppIcon";
import { useTenantContext } from "../../contexts/TenantContext";
import { useAuth } from "../../contexts/AuthContext";
import { policyGeneratorApi } from "../../services/api";

const SectionCard = ({ icon, title, children, className = "" }) => (
  <div className={`card ${className}`}>
    <div className="card-header">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name={icon} size={20} className="text-primary" />
        </div>
        <h2 className="card-title">{title}</h2>
      </div>
    </div>
    <div className="card-content">{children}</div>
  </div>
);

const CriterionRow = ({ criterion }) => (
  <div className="flex items-start gap-3 py-2">
    <div
      className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
        criterion.enabled
          ? "bg-success/10 text-success"
          : "bg-muted text-muted-foreground"
      }`}
    >
      <Icon name={criterion.enabled ? "Check" : "X"} size={14} />
    </div>
    <div>
      <p className="font-medium text-sm">{criterion.name}</p>
      <p className="text-xs text-muted-foreground">{criterion.description}</p>
    </div>
  </div>
);

const PolicyGenerator = () => {
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();
  const { isTenantAdmin, isTroncMaster } = useAuth();

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [policyDate, setPolicyDate] = useState("");
  const printRef = useRef(null);

  // Only TroncMaster, TenantAdmin, and GlobalAdmin can use the date selector
  const canUseDate = isGlobalAdmin || isTenantAdmin || isTroncMaster;

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Policy Generator", path: "/policy-generator" },
  ];

  const fetchPolicy = useCallback(async (ruleId = null, date = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await policyGeneratorApi.generate(ruleId, date || null);
      const data = response?.data ?? response?.Data ?? response;
      if (data) {
        setPolicy(data);
      } else {
        setError("No policy data returned.");
      }
    } catch (err) {
      console.error("[PolicyGenerator] Error:", err);
      setError(err.message || "Failed to generate policy document.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTenant || (!isGlobalAdmin && !needsTenantSelection)) {
      fetchPolicy(selectedRuleId, policyDate || null);
    }
  }, [
    selectedTenant?.id,
    fetchPolicy,
    isGlobalAdmin,
    needsTenantSelection,
    selectedRuleId,
    policyDate,
  ]);

  const handleRuleChange = useCallback((e) => {
    const val = e.target.value;
    setSelectedRuleId(val === "" ? null : val);
  }, []);

  const handleDateChange = useCallback((e) => {
    const val = e.target.value;
    setPolicyDate(val);
    // Clear specific rule selection when date changes — let the backend pick the correct one
    setSelectedRuleId(null);
  }, []);

  const handleDownloadPdf = useCallback(() => {
    if (!policy) return;

    const tenantName = policy.tenantName || "TRONC";
    const dateStr = new Date().toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const logoUrl = policy.organisation?.logoUrl;
    const org = policy.organisation || {};
    const legal = policy.legalSection;
    const mode = policy.documentMode || "Active";

    // Build full HTML document content
    const htmlContent = `
<div style="font-family: 'Georgia', 'Times New Roman', serif; color: #1a1a1a; font-size: 11pt; line-height: 1.6;">
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .pdf-header { text-align: center; margin-bottom: 28pt; padding-bottom: 18pt; border-bottom: 2pt solid #1a2b4b; }
    .pdf-header img.logo { max-height: 60pt; max-width: 200pt; margin-bottom: 12pt; }
    .pdf-header h1 { font-size: 20pt; font-weight: 700; color: #1a2b4b; margin-bottom: 4pt; }
    .pdf-header .subtitle { font-size: 12pt; color: #555; font-style: italic; }
.pdf-header .meta { font-size: 9pt; color: #999; margin-top: 8pt; }
    .pdf-header .mode-badge { display: inline-block; padding: 2pt 10pt; border-radius: 3pt; font-size: 9pt; font-weight: 600; margin-top: 6pt; }
    .mode-active { background: #dcfce7; color: #166534; }
    .mode-scheduled { background: #fef3c7; color: #92400e; }
    .mode-preview { background: #e0e7ff; color: #3730a3; }

    .company-details { text-align: center; font-size: 9pt; color: #666; margin-bottom: 20pt; }
    .company-details strong { color: #333; }

    .legal-preamble { font-size: 10pt; color: #333; margin-bottom: 20pt; padding: 12pt 16pt; background: #f8f9fa; border-left: 3pt solid #1a2b4b; }

    .legal-section { margin-bottom: 16pt; page-break-inside: avoid; }
    .legal-heading { font-size: 12pt; font-weight: 700; color: #1a2b4b; margin-bottom: 6pt; }
    .legal-text { font-size: 10.5pt; text-align: justify; }

    .section { margin-bottom: 20pt; page-break-inside: avoid; }
    .section-title { font-size: 13pt; font-weight: 700; margin-bottom: 10pt; padding-bottom: 4pt; border-bottom: 1pt solid #ddd; color: #1a2b4b; }

    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8pt; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8pt; }
    .grid-5 { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8pt; }

    .field { margin-bottom: 6pt; }
    .field-label { font-size: 8pt; color: #888; text-transform: uppercase; letter-spacing: 0.5pt; font-family: -apple-system, sans-serif; }
    .field-value { font-size: 10pt; font-weight: 500; }

    .badge { display: inline-block; padding: 1pt 6pt; border-radius: 3pt; font-size: 8pt; font-weight: 600; font-family: -apple-system, sans-serif; }
    .badge-success { background: #dcfce7; color: #166534; }
    .badge-warning { background: #fef3c7; color: #92400e; }
    .badge-neutral { background: #f3f4f6; color: #374151; }
    .badge-secondary { background: #e0e7ff; color: #3730a3; }

    .info-box { background: #f0f9ff; border: 1pt solid #bae6fd; border-radius: 4pt; padding: 8pt 12pt; font-size: 9pt; margin-bottom: 12pt; }
    .method-box { background: #f9fafb; border-radius: 4pt; padding: 8pt 12pt; font-size: 10pt; margin: 8pt 0; }
    .option-row { display: inline-block; padding: 3pt 8pt; border-radius: 3pt; font-size: 9pt; margin-right: 6pt; margin-bottom: 4pt; font-family: -apple-system, sans-serif; }
    .option-enabled { background: #dcfce7; }
    .option-disabled { background: #f3f4f6; color: #888; }

    .criterion-row { display: flex; align-items: flex-start; gap: 8pt; padding: 4pt 0; border-bottom: 0.5pt solid #f3f4f6; }
    .criterion-icon { width: 14pt; height: 14pt; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 8pt; font-weight: 700; flex-shrink: 0; margin-top: 2pt; font-family: -apple-system, sans-serif; }
    .criterion-on { background: #dcfce7; color: #166534; }
    .criterion-off { background: #f3f4f6; color: #999; }
    .criterion-name { font-weight: 500; font-size: 10pt; }
    .criterion-desc { font-size: 9pt; color: #666; }

    .role-row { display: flex; justify-content: space-between; align-items: center; padding: 3pt 8pt; border-radius: 3pt; margin-bottom: 3pt; font-size: 10pt; }
    .role-eligible { background: #f0fdf4; }
    .role-ineligible { background: #f9fafb; }
    .role-dept { font-size: 8pt; color: #888; margin-left: 6pt; }
    .role-pts { font-size: 9pt, font-family: monospace; }

    .stat-box { text-align: center; padding: 8pt; background: #f9fafb; border-radius: 4pt; }
    .stat-value { font-size: 18pt; font-weight: 700; }
    .stat-label { font-size: 8pt; color: #888; font-family: -apple-system, sans-serif; }

    table { width: 100%; border-collapse: collapse; font-size: 10pt; }
    th { text-align: left; font-weight: 600; padding: 4pt 8pt; border-bottom: 1pt solid #ddd; }
  td { padding: 4pt 8pt; border-bottom: 0.5pt solid #f3f4f6; }

    .compliance-row { display: flex; align-items: flex-start; gap: 6pt; font-size: 9pt; color: #555; margin-bottom: 4pt; }
    .compliance-icon { flex-shrink: 0; margin-top: 2pt; }

    .dept-row { display: flex; justify-content: space-between; padding: 3pt 0; border-bottom: 0.5pt solid #f3f4f6; font-size: 10pt; }

    .pdf-footer { margin-top: 32pt; padding-top: 12pt; border-top: 1pt solid #ddd; text-align: center; font-size: 8pt; color: #999; }

    .scheduled-box { background: #fffbeb; border: 1pt solid #fde68a; border-radius: 4pt; padding: 6pt 10pt; font-size: 9pt; margin-top: 8pt; }

    .roles-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16pt; }
    .roles-heading { font-size: 10pt; font-weight: 600; margin-bottom: 6pt; display: flex; align-items: center; gap: 4pt; }
    .roles-dot { width: 6pt; height: 6pt; border-radius: 50%; display: inline-block; }
    .roles-dot-green { background: #22c55e; }
    .roles-dot-grey { background: #9ca3af; }

    .signature-block { margin-top: 40pt; display: grid; grid-template-columns: 1fr 1fr; gap: 40pt; }
  .signature-line { border-top: 1pt solid #333; padding-top: 4pt; font-size: 9pt; color: #555; margin-top: 40pt; }

    .intro-section { margin-bottom: 20pt; font-size: 10.5pt; line-height: 1.7; }
    .intro-section h2 { font-size: 14pt; font-weight: 700; color: #1a2b4b; margin-bottom: 8pt; }
    .intro-section p { margin-bottom: 8pt; text-align: justify; }
    .intro-section ul { margin-left: 18pt; margin-bottom: 10pt; }
    .intro-section ul li { margin-bottom: 4pt; }
  </style>

  <div class="pdf-header">
    ${logoUrl ? `<img class="logo" src="${esc(logoUrl)}" alt="${esc(tenantName)}" onerror="this.style.display='none'" />` : ""}
    <h1>${esc(tenantName)}</h1>
    <div class="subtitle">TRONC Distribution Policy Document</div>
    <span class="mode-badge ${mode === "Active" ? "mode-active" : mode === "Scheduled" ? "mode-scheduled" : "mode-preview"}">${esc(mode)} Policy</span>
    <div class="meta">Generated ${dateStr} &bull; Document reference: TRONC-POL-${policy.tenantSlug?.toUpperCase() || "XXX"}-${new Date().getFullYear()}</div>
  </div>

  <div class="company-details">
    <strong>${esc(org.companyName)}</strong><br/>
    ${org.address ? `${esc(org.address)}<br/>` : ""}
    ${org.contactEmail ? `Email: ${esc(org.contactEmail)}` : ""}${org.contactPhone ? ` &bull; Tel: ${esc(org.contactPhone)}` : ""}
    ${org.companyRegistrationNumber ? `<br/>Company Reg: ${esc(org.companyRegistrationNumber)}` : ""}${org.vatNumber ? ` &bull; VAT: ${esc(org.vatNumber)}` : ""}
  </div>

  <div class="intro-section">
  <h2>1. Introduction &amp; Purpose</h2>
    <p>This document sets out the TRONC distribution policy for <strong>${esc(tenantName)}</strong>. It describes how gratuities, tips, and service charges collected by the business are pooled and distributed to eligible employees in a fair, transparent, and HMRC-compliant manner.</p>
    <p>The TRONC scheme operates independently of the employer. A designated Troncmaster is responsible for overseeing the fair allocation of funds in accordance with this policy and relevant HMRC guidelines (specifically the treatment of tips, gratuities, and service charges under PAYE regulations).</p>

    <h2>2. Scope</h2>
    <p>This policy applies to all employees of ${esc(tenantName)} who meet the eligibility criteria set out in Section 4 below. It covers:</p>
    <ul>
      <li>How TRONC funds are collected and recorded</li>
 <li>The method used to distribute funds among eligible staff</li>
      <li>Eligibility requirements for participation in the scheme</li>
    <li>The role of the Troncmaster and their responsibilities</li>
      <li>Record-keeping and compliance obligations</li>
    </ul>

 <h2>3. TRONC Collection &amp; Recording</h2>
    <p>All card tips, cash tips, and discretionary service charges are recorded by the business&rsquo;s point-of-sale system and imported into the Rocket Tronc management platform. Daily TRONC totals are calculated per location and reconciled before distribution.</p>
    <p>Cash tips declared by employees are included in the pool where applicable. Mandatory service charges retained by the business are <strong>not</strong> included in the TRONC pool.</p>
  </div>

  ${legal ? buildLegalSections(legal) : ""}

  <div class="intro-section">
    <h2>4. Eligibility Criteria</h2>
    <p>To be eligible for TRONC distributions, an employee must satisfy the criteria configured in the system. The current eligibility settings are documented in Appendix C. In general, eligibility is determined by employment status, role classification, and any minimum service requirements.</p>

    <h2>5. Distribution Method</h2>
    <p>The method by which TRONC is divided among eligible employees is determined by the active distribution rule configured in the system. The current active rule and its parameters are documented in Appendix B. The system supports equal division, role-based point weighting, and department-based allocation methods.</p>
    <p>Where a points-based system is used, each eligible job role is assigned a weighting (see Appendix D). An employee&rsquo;s share is proportional to their points relative to the total points pool. Adjustments for hours worked and seniority may apply where enabled.</p>

    <h2>6. Troncmaster Responsibilities</h2>
    <p>The designated Troncmaster is responsible for:</p>
    <ul>
      <li>Ensuring TRONC is distributed fairly and in accordance with this policy</li>
      <li>Reviewing and approving distribution calculations before payment</li>
  <li>Maintaining accurate records of all TRONC receipts and distributions</li>
      <li>Reporting TRONC payments to HMRC where required (National Insurance obligations)</li>
      <li>Communicating any policy changes to affected employees</li>
    </ul>

    <h2>7. Record Keeping &amp; Compliance</h2>
    <p>All TRONC transactions, distribution calculations, and employee allocations are recorded in the Rocket Tronc platform with full audit trails. Records are retained for a minimum of 6 years in compliance with HMRC requirements. The system maintains immutable audit logs of all changes.</p>

    <h2>8. Dispute Resolution</h2>
    <p>Any employee who believes their TRONC allocation is incorrect should raise the matter with the Troncmaster in the first instance. If the matter cannot be resolved informally, it should be escalated through the company&rsquo;s standard grievance procedure.</p>
  </div>

  <div style="margin:24pt 0;border-top:1pt solid #ddd;padding-top:16pt">
    <div class="section-title">Appendices &mdash; System Configuration Details</div>
    <p style="font-size:9pt;color:#666;margin-bottom:12pt">The following appendices document the current system configuration that underpins this policy. They are auto-generated from live data.</p>
  </div>

  ${buildPdfSections(policy)}

  <div class="signature-block">
    <div>
   <div class="signature-line">Signed by (Troncmaster / Authorised Person)</div>
    </div>
    <div>
      <div class="signature-line">Date</div>
    </div>
  </div>

  <div class="pdf-footer">
    <p>This document was auto-generated by Rocket Tronc from the live system configuration on ${dateStr}.</p>
    <p>Changes to distribution rules, eligibility settings, or job roles will be reflected when the document is regenerated.</p>
    <p>Document reference: TRONC-POL-${policy.tenantSlug?.toUpperCase() || "XXX"}-${new Date().getFullYear()} &bull; Confidential</p>
  </div>
</div>`;

    // Create a temporary container for html2pdf
    const container = document.createElement("div");
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    const filename = `TRONC-Policy-${policy.tenantSlug || "document"}-${new Date().toISOString().split("T")[0]}.pdf`;

    html2pdf()
      .set({
        margin: [15, 15, 15, 15],
        filename: filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      })
      .from(container)
      .save()
      .then(() => {
        document.body.removeChild(container);
      })
      .catch((err) => {
        console.error("PDF generation failed:", err);
        document.body.removeChild(container);
      });
  }, [policy]);

  // ─── Global Admin empty state ─────────────────────────────────────
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Policy Generator - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Policy Generator"
          description="Generate compliant TRONC policy documents."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to generate policy documents.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  // ─── Loading ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Policy Generator - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Policy Generator"
          description="Generating policy document..."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">
              Generating policy document from live configuration...
            </p>
          </div>
        </PageLayout>
      </>
    );
  }

  // ─── Error ─────────────────────────────────────────────────────────
  if (error) {
    return (
      <>
        <Helmet>
          <title>Policy Generator - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Policy Generator"
          description="Generate compliant TRONC policy documents."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-error" />
            </div>
            <h2 className="text-xl font-bold mb-2">Generation Failed</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => fetchPolicy(selectedRuleId)}
              className="btn btn-primary"
            >
              <Icon name="RefreshCw" size={16} />
              Retry
            </button>
          </div>
        </PageLayout>
      </>
    );
  }

  if (!policy) return null;

  const p = policy;
  const availableRules = p.availableRules || [];

  return (
    <>
      <Helmet>
        <title>
          Policy Generator - {selectedTenant?.name || "Rocket Tronc"}
        </title>
      </Helmet>

      <PageLayout
        title="TRONC Policy Document"
        description={`Auto-generated from ${p.tenantName} system configuration`}
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Date Selector — TroncMaster, TenantAdmin, GlobalAdmin only */}
            {canUseDate && (
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                <label className="text-xs text-muted-foreground break-words">
                  Policy Date:
                </label>
                <input
                  type="date"
                  value={policyDate}
                  onChange={handleDateChange}
                  className="h-9 px-3 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                {policyDate && (
                  <button
                    onClick={() => {
                      setPolicyDate("");
                      setSelectedRuleId(null);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                    title="Clear date filter"
                  >
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>
            )}

            {/* Rule Selector */}
            {availableRules.length > 1 && (
              <select
                value={selectedRuleId || ""}
                onChange={handleRuleChange}
                className="min-w-0 h-9 px-3 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Active Rule</option>
                {availableRules.map((rule) => (
                  <option key={rule.id} value={rule.id}>
                    {rule.name} ({rule.status})
                    {rule.effectiveFrom
                      ? ` — ${new Date(rule.effectiveFrom).toLocaleDateString("en-GB")}`
                      : ""}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={handleDownloadPdf}
              className="btn btn-primary btn-sm"
              title="Export as PDF"
            >
              <Icon name="Download" size={16} />
              Export PDF
            </button>

            <button
              onClick={() => fetchPolicy(selectedRuleId, policyDate || null)}
              className="btn btn-outline btn-sm"
            >
              <Icon name="RefreshCw" size={16} />
              Regenerate
            </button>
          </div>
        }
      >
        {/* Wrap all renderable content in a ref for print */}
        <div ref={printRef}>
          {/* Date selection banner */}
          {policyDate && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-start gap-3 mb-4">
              <Icon name="Calendar" size={18} className="text-primary mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-primary">
                  Historical Policy View
                </p>
                <p className="text-muted-foreground">
                  This policy document is generated using the eligibility
                  criteria and distribution rule that were effective on{" "}
                  <strong>
                    {new Date(policyDate).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </strong>
                  . Clear the date to view the current active configuration.
                </p>
              </div>
            </div>
          )}

          {/* Mode banner */}
          {p.documentMode && p.documentMode !== "Active" && (
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 flex items-start gap-3 mb-4">
              <Icon name="Clock" size={18} className="text-warning mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-warning">
                  {p.documentMode === "Scheduled"
                    ? "Scheduled Rule Policy"
                    : "Policy Preview"}
                </p>
                <p className="text-muted-foreground">
                  This document is generated for the{" "}
                  {p.documentMode.toLowerCase()} rule "
                  {p.distributionMethod?.activeRuleName}". It shows what the
                  policy will look like
                  {p.documentMode === "Scheduled"
                    ? " when this rule becomes active"
                    : ""}
                  .
                </p>
              </div>
            </div>
          )}

          {/* Meta Banner */}
          <div className="bg-info/5 border border-info/20 rounded-lg p-4 flex items-start gap-3 mb-6">
            <Icon name="Info" size={18} className="text-info mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-info">
                Auto-Generated Policy Document
              </p>
              <p className="text-muted-foreground">
                Generated on{" "}
                {new Date(p.generatedAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                . This document reflects your current system configuration.
                Changes to rules, roles, or eligibility settings will be
                reflected when you regenerate. Use "Export PDF" to save a
                compliant copy.
              </p>
            </div>
          </div>

          {/* 1: Organisation */}
          <PageSection>
            <SectionCard icon="Building" title="1. Organisation Details">
              {p.organisation?.logoUrl && (
                <div className="mb-4">
                  <img
                    src={p.organisation.logoUrl}
                    alt={p.organisation?.companyName}
                    className="h-12 w-auto"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Company Name</p>
                  <p className="font-medium">{p.organisation?.companyName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact Email</p>
                  <p className="font-medium">{p.organisation?.contactEmail}</p>
                </div>
                {p.organisation?.contactPhone && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Contact Phone
                    </p>
                    <p className="font-medium">{p.organisation.contactPhone}</p>
                  </div>
                )}
                {p.organisation?.companyRegistrationNumber && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Company Reg.
                    </p>
                    <p className="font-medium">
                      {p.organisation.companyRegistrationNumber}
                    </p>
                  </div>
                )}
                {p.organisation?.vatNumber && (
                  <div>
                    <p className="text-sm text-muted-foreground">VAT Number</p>
                    <p className="font-medium">{p.organisation.vatNumber}</p>
                  </div>
                )}
                {p.organisation?.address && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium">{p.organisation.address}</p>
                  </div>
                )}
              </div>

              {p.organisation?.locations?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold mb-3">
                    Active Locations ({p.organisation.locationCount})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {p.organisation.locations.map((loc, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 p-3 bg-muted rounded-lg text-sm"
                      >
                        <Icon
                          name="MapPin"
                          size={14}
                          className="text-primary mt-0.5"
                        />
                        <div>
                          <p className="font-medium">{loc.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {loc.code}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>
          </PageSection>

          {/* 2: Distribution Method */}
          <PageSection>
            <SectionCard icon="PieChart" title="2. Distribution Method">
              {p.distributionMethod?.activeRuleName ? (
                <>
                  <div className="bg-success/10 border border-success/25 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="badge badge-success">
                      {p.documentMode === "Scheduled" ? "Scheduled" : "Active"}
                    </span>
                    <span className="font-semibold">
                      {p.distributionMethod.activeRuleName}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ({p.distributionMethod.activeRuleType})
                    </span>
                  </div>
                  {p.distributionMethod.activeRuleDescription && (
                    <p className="text-sm text-muted-foreground mb-4">
                      {p.distributionMethod.activeRuleDescription}
                    </p>
                  )}
                  <div className="bg-success/5 border border-success/20 rounded-lg p-4 text-sm mb-4">
                    {p.distributionMethod.methodDescription}
                  </div>

                  {/* Department allocations */}
                  {p.distributionMethod.departmentAllocations?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold mb-2">
                        Department Allocations
                      </h4>
                      <div className="space-y-1">
                        {p.distributionMethod.departmentAllocations.map(
                          (alloc, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between px-3 py-2 rounded-md bg-success/5 border border-success/15"
                            >
                              <span className="text-sm">
                                {alloc.department}
                              </span>
                              <span className="font-semibold text-sm text-success">
                                {alloc.percentage}%
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                  {/* Universal options */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div
                      className={`flex items-center gap-2 p-2 rounded ${
                        p.distributionMethod.enableMinimumFloor
                          ? "bg-success/10"
                          : "bg-muted"
                      }`}
                    >
                      <Icon
                        name={
                          p.distributionMethod.enableMinimumFloor
                            ? "Check"
                            : "X"
                        }
                        size={14}
                      />
                      <span>
                        Min Floor{" "}
                        {p.distributionMethod.enableMinimumFloor
                          ? `£${p.distributionMethod.minimumFloorAmount}`
                          : "Off"}
                      </span>
                    </div>
                    <div
                      className={`flex items-center gap-2 p-2 rounded ${
                        p.distributionMethod.enableHourFactor
                          ? "bg-success/10"
                          : "bg-muted"
                      }`}
                    >
                      <Icon
                        name={
                          p.distributionMethod.enableHourFactor ? "Check" : "X"
                        }
                        size={14}
                      />
                      <span>
                        Hour Factor{" "}
                        {p.distributionMethod.enableHourFactor
                          ? `(${(
                              p.distributionMethod.hourFactorWeight * 100
                            ).toFixed(0)}%)`
                          : "Off"}
                      </span>
                    </div>
                    <div
                      className={`flex items-center gap-2 p-2 rounded ${
                        p.distributionMethod.enableSeniorityFactor
                          ? "bg-success/10"
                          : "bg-muted"
                      }`}
                    >
                      <Icon
                        name={
                          p.distributionMethod.enableSeniorityFactor
                            ? "Check"
                            : "X"
                        }
                        size={14}
                      />
                      <span>
                        Seniority{" "}
                        {p.distributionMethod.enableSeniorityFactor
                          ? `(${(
                              p.distributionMethod.seniorityFactorWeight * 100
                            ).toFixed(0)}%)`
                          : "Off"}
                      </span>
                    </div>
                  </div>
                  </div>{/* end green rule wrapper */}

                  {p.distributionMethod.scheduledRuleName && (
                    <div className="mt-4 bg-warning/5 border border-warning/20 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={14} className="text-warning" />
                        <span className="text-sm font-medium">
                          Scheduled Change
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        "{p.distributionMethod.scheduledRuleName}" (
                        {p.distributionMethod.scheduledRuleType}) will become
                        active on{" "}
                        {new Date(
                          p.distributionMethod.scheduledRuleEffectiveFrom,
                        ).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 text-center">
                  <Icon
                    name="AlertTriangle"
                    size={24}
                    className="text-warning mx-auto mb-2"
                  />
                  <p className="text-sm font-medium">
                    No Active Distribution Rule
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Configure a distribution rule to populate this section.
                  </p>
                </div>
              )}
            </SectionCard>
          </PageSection>

          {/* 3: Eligibility Criteria */}
          <PageSection>
            <SectionCard icon="UserCheck" title="3. Eligibility Criteria">
              <div className="divide-y divide-border">
                {p.eligibilityCriteria?.criteria?.map((c, i) => (
                  <CriterionRow key={i} criterion={c} />
                ))}
              </div>
            </SectionCard>
          </PageSection>

          {/* 4: Job Roles */}
          <PageSection>
            <SectionCard icon="Briefcase" title="4. Job Roles">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Eligible */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-success" />
                    Eligible Roles ({p.jobRoles?.eligibleRoles?.length || 0})
                  </h3>
                  <div className="space-y-2">
                    {p.jobRoles?.eligibleRoles?.map((role, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between py-1.5 px-3 bg-success/5 rounded text-sm"
                      >
                        <div>
                          <span className="font-medium">{role.name}</span>
                          {role.department && (
                            <span className="text-xs text-muted-foreground ml-2">
                              {role.department}
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-mono">
                          {role.isManagement ? "Mgmt" : "Staff"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ineligible */}
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                    Ineligible Roles ({p.jobRoles?.ineligibleRoles?.length || 0}
                    )
                  </h3>
                  <div className="space-y-2">
                    {p.jobRoles?.ineligibleRoles?.map((role, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between py-1.5 px-3 bg-muted rounded text-sm"
                      >
                        <span>{role.name}</span>
                        {role.department && (
                          <span className="text-xs text-muted-foreground">
                            {role.department}
                          </span>
                        )}
                      </div>
                    ))}
                    {(!p.jobRoles?.ineligibleRoles ||
                      p.jobRoles.ineligibleRoles.length === 0) && (
                      <p className="text-sm text-muted-foreground italic">
                        All roles are eligible.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </SectionCard>
          </PageSection>

          {/* 5: Statistics */}
          <PageSection>
            <SectionCard icon="BarChart3" title="5. Current Statistics">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {[
                  {
                    label: "Active Employees",
                    value: p.statistics?.totalActiveEmployees,
                  },
                  {
                    label: "Eligible Employees",
                    value: p.statistics?.totalEligibleEmployees,
                  },
                  {
                    label: "Active Roles",
                    value: p.statistics?.totalActiveJobRoles,
                  },
                  {
                    label: "Eligible Roles",
                    value: p.statistics?.totalEligibleJobRoles,
                  },
                  {
                    label: "Locations",
                    value: p.statistics?.totalActiveLocations,
                  },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{stat.value ?? 0}</p>
                    <p className="text-xs text-muted-foreground">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </SectionCard>
          </PageSection>

          {/* 6: Rule History */}
          {p.allRules?.length > 0 && (
            <PageSection>
              <SectionCard icon="History" title="6. Distribution Rule History">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="pb-2 font-medium">Rule</th>
                        <th className="pb-2 font-medium">Type</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium">Effective From</th>
                        <th className="pb-2 font-medium">Effective To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.allRules.map((rule, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-2 font-medium">{rule.name}</td>
                          <td className="py-2">{rule.type}</td>
                          <td className="py-2">
                            <span
                              className={`badge ${
                                rule.status === "Active"
                                  ? "badge-success"
                                  : rule.status === "Scheduled"
                                    ? "badge-warning"
                                    : rule.status === "Draft"
                                      ? "badge-secondary"
                                      : "badge-neutral"
                              }`}
                            >
                              {rule.status}
                            </span>
                          </td>
                          <td className="py-2 text-muted-foreground">
                            {rule.effectiveFrom
                              ? new Date(rule.effectiveFrom).toLocaleDateString(
                                  "en-GB",
                                )
                              : "—"}
                          </td>
                          <td className="py-2 text-muted-foreground">
                            {rule.effectiveTo
                              ? new Date(rule.effectiveTo).toLocaleDateString(
                                  "en-GB",
                                )
                              : "Open-ended"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </PageSection>
          )}

          {/* 7: Compliance Notes */}
          {p.complianceNotes?.length > 0 && (
            <PageSection>
              <SectionCard icon="Shield" title="7. Compliance Notes">
                <div className="space-y-2">
                  {p.complianceNotes.map((note, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <Icon
                        name={
                          note.startsWith("WARNING")
                            ? "AlertTriangle"
                            : note.startsWith("NOTE")
                              ? "Info"
                              : "CheckCircle"
                        }
                        size={14}
                        className={`mt-0.5 flex-shrink-0 ${
                          note.startsWith("WARNING")
                            ? "text-error"
                            : note.startsWith("NOTE")
                              ? "text-warning"
                              : "text-success"
                        }`}
                      />
                      <span className="text-muted-foreground">{note}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </PageSection>
          )}
        </div>
        {/* end printRef wrapper */}
      </PageLayout>
    </>
  );
};

// ─── Legal sections for PDF ──────────────────────────────────────────
function buildLegalSections(legal) {
  if (!legal) return "";
  let html = "";
  if (legal.preamble) {
    html += `<div class="legal-preamble">${esc(legal.preamble)}</div>`;
  }
  if (legal.sections?.length > 0) {
    legal.sections.forEach((s) => {
      html += `<div class="legal-section"><div class="legal-heading">${esc(s.heading)}</div><div class="legal-text">${esc(s.text)}</div></div>`;
    });
  }
  // Removed duplicate appendix reference - it's already in the main htmlContent
  return html;
}

// ─── PDF content builder (plain HTML for print window) ──────────────
function buildPdfSections(p) {
  let html = "";

  // Appendix A: Organisation
  html += `<div class="section">
  <div class="section-title">Appendix A: Organisation & Locations</div>
    <div class="grid-2">
    <div class="field"><div class="field-label">Company Name</div><div class="field-value>${esc(p.organisation?.companyName)}</div></div>
      <div class="field"><div class="field-label">Contact Email</div><div class="field-value>${esc(p.organisation?.contactEmail)}</div></div>
      ${p.organisation?.companyRegistrationNumber ? `<div class="field"><div class="field-label">Company Reg.</div><div class="field-value>${esc(p.organisation.companyRegistrationNumber)}</div></div>` : ""}
      ${p.organisation?.vatNumber ? `<div class="field"><div class="field-label">VAT Number</div><div class="field-value>${esc(p.organisation.vatNumber)}</div></div>` : ""}
    </div>
    ${p.organisation?.address ? `<div class="field" style="margin-top:6pt"><div class="field-label">Address</div><div class="field-value>${esc(p.organisation.address)}</div></div>` : ""}
    ${
      p.organisation?.locations?.length > 0
        ? `
      <div style="margin-top:12pt">
    <div style="font-size:10pt;font-weight:600;margin-bottom:6pt">Active Locations (${p.organisation.locationCount})</div>
        <div class="grid-3">
          ${p.organisation.locations.map((loc) => `<div style="padding:4pt 8pt;background:#f9fafb;border-radius:3pt;font-size:9pt"><strong>${esc(loc.name)}</strong><br/><span style="color:#888">${esc(loc.code)}</span></div>`).join("")}
   </div>
      </div>`
        : ""
    }
  </div>`;

  // Appendix B: Distribution Method
  html += `<div class="section"><div class="section-title">Appendix B: Distribution Configuration</div>`;
  if (p.distributionMethod?.activeRuleName) {
    html += `<div style="margin-bottom:6pt"><span class="badge badge-success">${p.documentMode === "Scheduled" ? "Scheduled" : "Active"}</span> <strong>${esc(p.distributionMethod.activeRuleName)}</strong> <span style="color:#888;font-size:9pt">(${esc(p.distributionMethod.activeRuleType)})</span></div>`;
    if (p.distributionMethod.activeRuleDescription) {
      html += `<div style="font-size:9pt;color:#666;margin-bottom:6pt">${esc(p.distributionMethod.activeRuleDescription)}</div>`;
    }
    html += `<div class="method-box">${esc(p.distributionMethod.methodDescription)}</div>`;
    if (p.distributionMethod.departmentAllocations?.length > 0) {
      html += `<div style="margin:8pt 0"><div style="font-size:10pt;font-weight:600;margin-bottom:4pt">Department Allocations</div>`;
      p.distributionMethod.departmentAllocations.forEach((a) => {
        html += `<div class="dept-row"><span>${esc(a.department)}</span><strong>${a.percentage}%</strong></div>`;
      });
      html += `</div>`;
    }
    html += `<div style="margin-top:8pt">`;
    html += `<span class="option-row ${p.distributionMethod.enableMinimumFloor ? "option-enabled" : "option-disabled"}">${p.distributionMethod.enableMinimumFloor ? "✓" : "✗"} Min Floor ${p.distributionMethod.enableMinimumFloor ? "£" + (p.distributionMethod.minimumFloorAmount || 0) : "Off"}</span>`;
    html += `<span class="option-row ${p.distributionMethod.enableHourFactor ? "option-enabled" : "option-disabled"}">${p.distributionMethod.enableHourFactor ? "✓" : "✗"} Hour Factor ${p.distributionMethod.enableHourFactor ? "(" + ((p.distributionMethod.hourFactorWeight || 0) * 100).toFixed(0) + "%)" : "Off"}</span>`;
    html += `<span class="option-row ${p.distributionMethod.enableSeniorityFactor ? "option-enabled" : "option-disabled"}">${p.distributionMethod.enableSeniorityFactor ? "✓" : "✗"} Seniority ${p.distributionMethod.enableSeniorityFactor ? "(" + ((p.distributionMethod.seniorityFactorWeight || 0) * 100).toFixed(0) + "%)" : "Off"}</span>`;
    html += `</div>`;
    if (p.distributionMethod.scheduledRuleName) {
      const schedDate = p.distributionMethod.scheduledRuleEffectiveFrom
        ? new Date(
            p.distributionMethod.scheduledRuleEffectiveFrom,
          ).toLocaleDateString("en-GB")
        : "—";
      html += `<div class="scheduled-box">⏰ <strong>Scheduled Change:</strong> "${esc(p.distributionMethod.scheduledRuleName)}" (${esc(p.distributionMethod.scheduledRuleType)}) effective ${schedDate}</div>`;
    }
  } else {
    html += `<div class="info-box" style="background:#fffbeb;border-color:#fde68a">⚠ No active distribution rule is currently configured.</div>`;
  }
  html += `</div>`;

  // Appendix C: Eligibility Criteria
  html += `<div class="section"><div class="section-title">Appendix C: Eligibility Criteria</div>`;
  (p.eligibilityCriteria?.criteria || []).forEach((c) => {
    html += `<div class="criterion-row"><div class="criterion-icon ${c.enabled ? "criterion-on" : "criterion-off"}">${c.enabled ? "✓" : "✗"}</div><div><div class="criterion-name">${esc(c.name)}</div><div class="criterion-desc">${esc(c.description)}</div></div></div>`;
  });
  html += `</div>`;

  // Appendix D: Job Roles
  const eligibleRoles = p.jobRoles?.eligibleRoles || [];
  const ineligibleRoles = p.jobRoles?.ineligibleRoles || [];
  html += `<div class="section"><div class="section-title">Appendix D: Job Roles & Eligibility</div><div class="roles-grid"><div>`;
  html += `<div class="roles-heading"><span class="roles-dot roles-dot-green"></span> Eligible Roles (${eligibleRoles.length})</div>`;
  html += eligibleRoles
    .map(
      (r) =>
        `<div class="role-row role-eligible"><div><strong>${esc(r.name)}</strong>${r.department ? `<span class="role-dept">${esc(r.department)}</span>` : ""}</div><span class="role-pts">${r.isManagement ? "Mgmt" : "Staff"}</span></div>`,
    )
    .join("");
  html += `</div><div>`;
  html += `<div class="roles-heading"><span class="roles-dot roles-dot-grey"></span> Ineligible Roles (${ineligibleRoles.length})</div>`;
  if (ineligibleRoles.length > 0) {
    html += ineligibleRoles
      .map(
        (r) =>
          `<div class="role-row role-ineligible"><span>${esc(r.name)}</span>${r.department ? `<span class="role-dept">${esc(r.department)}</span>` : ""}</div>`,
      )
      .join("");
  } else {
    html += `<div style="font-size:9pt;color:#888;font-style:italic">All roles are eligible.</div>`;
  }
  html += `</div></div></div>`;

  // Appendix E: Statistics
  const stats = [
    { label: "Active Employees", value: p.statistics?.totalActiveEmployees },
    {
      label: "Eligible Employees",
      value: p.statistics?.totalEligibleEmployees,
    },
    { label: "Active Roles", value: p.statistics?.totalActiveJobRoles },
    { label: "Eligible Roles", value: p.statistics?.totalEligibleJobRoles },
    { label: "Locations", value: p.statistics?.totalActiveLocations },
  ];
  html += `<div class="section"><div class="section-title">Appendix E: Statistics Snapshot</div><div class="grid-5">`;
  html += stats
    .map(
      (s) =>
        `<div class="stat-box"><div class="stat-value">${s.value ?? 0}</div><div class="stat-label">${s.label}</div></div>`,
    )
    .join("");
  html += `</div></div>`;

  // Appendix F: Rule History
  if (p.allRules?.length > 0) {
    html += `<div class="section"><div class="section-title">Appendix F: Distribution Rule History</div><table><thead><tr><th>Rule</th><th>Type</th><th>Status</th><th>Effective From</th><th>Effective To</th></tr></thead><tbody>`;
    html += p.allRules
      .map((r) => {
        const bc =
          r.status === "Active"
            ? "badge-success"
            : r.status === "Scheduled"
              ? "badge-warning"
              : r.status === "Draft"
                ? "badge-secondary"
                : "badge-neutral";
        return `<tr><td><strong>${esc(r.name)}</strong></td><td>${esc(r.type)}</td><td><span class="badge ${bc}">${esc(r.status)}</span></td><td>${r.effectiveFrom ? new Date(r.effectiveFrom).toLocaleDateString("en-GB") : "—"}</td><td>${r.effectiveTo ? new Date(r.effectiveTo).toLocaleDateString("en-GB") : "Open-ended"}</td></tr>`;
      })
      .join("");
    html += `</tbody></table></div>`;
  }

  // Appendix G: Compliance Notes
  if (p.complianceNotes?.length > 0) {
    html += `<div class="section"><div class="section-title">Appendix G: Compliance Notes</div>`;
    html += p.complianceNotes
      .map((note) => {
        const icon = note.startsWith("WARNING")
          ? "⚠"
          : note.startsWith("NOTE")
            ? "ℹ"
            : "✓";
        return `<div class="compliance-row"><span class="compliance-icon">${icon}</span><span>${esc(note)}</span></div>`;
      })
      .join("");
    html += `</div>`;
  }

  return html;
}

function esc(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default PolicyGenerator;
